import {
	FormsEntities,
	MainUtils,
	MessageBox,
	MessageBoxButtons,
	Mobile,
	getMLProperty,
	onPathOpenedAction,
	panelType,
	ErrorInfo
} from '@bimser/common';
import { BCNotification, IFlexPanel, findMessage } from '@bimser/components';
import { createGuid, extend, isNullOrUndefined } from '@bimser/core';
import { List, Map } from 'immutable';
import isEmpty from 'lodash/isEmpty';
import { call, delay, put, select } from 'redux-saga/effects';
import { closeUnpinnedFlexPanelsTransaction } from '../../app-main/actions';
import addFlexPanelAction from '../../app-main/actions/addFlexPanelTransactionAction';
import { closeFlexPanelTransaction } from '../../app-main/sagas/closeFlexPanelTransaction';
import { WebInterfaceBaseState } from '../../common/base/state';
import resetSearchDataAction from '../../modules/dm/components/dashboard/components/advancedSearch/actions/resetSearchDataAction';
import { OpenFlowViewer } from '../../modules/workflow/components/flowViewer/sagas/helper/openFlowViewer';
import {
	initializePanelManagerTransaction,
	panelAddNewToStoreAction,
	panelRemoveFromStoreAction,
	panelTellClosedToParentPanelAction,
	panelWillUnMountTransaction
} from '../actions';
import { FormWillCloseAction } from '../components/appForm/actions/index';
import { IOpenNewPanelPayload, IRelatedPanel, WebInterfaceAction } from '../entities';
import PanelComponentList from '../panelComponents';

export function* openNewPanel(payload: IOpenNewPanelPayload) {
	let {
		paneltype,
		fastEventId,
		role,
		headerHide,
		forMobile,
		MaxButtonHide,
		canHandleCloseEvent,
		extraProps,
		initialSize,
		isCloseButtonHide,
		parent,
		size,
		title,
		subtitle,
		addNewToStore,
		guid,
		HeaderActionsHide,
		disableResize,
		titleHide,
		transparentBackground,
		className
	} = payload;
	guid = guid || createGuid();
	const _paneltype = paneltype;
	const key = MainUtils.GetComponentKeyFromPanelType(_paneltype);
	const _props = {
		panelGuid: guid,
		parentPanel: parent,
		panelType: _paneltype,
		actionRole: role
	};
	headerHide = forMobile ? true : headerHide;
	const composedProps = extend({}, _props, extraProps, true);
	size = forMobile ? 3 : size;
	initialSize = forMobile ? 3 : initialSize;
	HeaderActionsHide = forMobile ? true : HeaderActionsHide;
	const panelData: IFlexPanel = {
		id: guid,
		size: size ? size : 1,
		initialSize: initialSize ? initialSize : 1,
		isActive: false,
		isOnScreen: true,
		isVisible: true,
		isMaximized: false,
		canHandleCloseEvent: isNullOrUndefined(canHandleCloseEvent) ? true : canHandleCloseEvent,
		isStartupPanel: _paneltype === panelType.startupLayout ? true : false,
		isMaximizeButtonHide: MaxButtonHide ? MaxButtonHide : false,
		isHeaderAreaHide: headerHide ? headerHide : false,
		isHeaderActionsHide: HeaderActionsHide,
		isTransparentBackground: transparentBackground,
		title: title,
		subtitle: subtitle,
		fromMobile: forMobile,
		className: className,
		componentKeys: [key],
		componentProps: [
			{
				componentKey: key,
				props: composedProps
			}
		],
		disableResize: disableResize,
		isTitleHide: titleHide,
		ghostPanel: fastEventId,
		isCloseButtonHide: isCloseButtonHide ?? false
	};

	if (PanelComponentList[key] && PanelComponentList[key].onPanelOpening) {
		yield PanelComponentList[key].onPanelOpening(panelData);
	}

	yield put(addFlexPanelAction(panelData));
	yield call(
		initializePanelManager,
		MainUtils.GetPanelTypeFromComponentKey(key),
		guid,
		composedProps
	);
	if (typeof addNewToStore == 'boolean' && addNewToStore) {
		yield put(
			panelAddNewToStoreAction({
				panelId: guid,
				panelType: _paneltype
			})
		);
	} else if (typeof addNewToStore == 'object' && addNewToStore.enable) {
		let panelData = addNewToStore.panelData;
		if (addNewToStore.setPanelDataPanelId && panelData.set) {
			panelData = panelData.set('panelId', guid);
		}
		yield put(
			panelAddNewToStoreAction({
				panelId: guid,
				panelType: _paneltype,
				data: panelData
			})
		);
	}
	yield put(
		onPathOpenedAction({
			path: panelType[_paneltype].toString(),
			panelId: guid,
			props: extraProps
		})
	);
	return guid;
}

export function* panelWillCloseActions(
	panelId: string,
	type: panelType,
	parentPanel: IRelatedPanel
) {
	yield put(
		panelTellClosedToParentPanelAction({
			panelId: panelId,
			panelType: type,
			data: parentPanel
		})
	);
	yield put(
		panelRemoveFromStoreAction({
			panelId: panelId,
			panelType: type
		})
	);
	yield put(resetSearchDataAction());
}

export function* initializePanelManager(panelType: panelType, panelId?: string, data?: any) {
	yield put(
		initializePanelManagerTransaction({
			panelType: panelType,
			panelId: panelId,
			data: data
		})
	);
}

export function* ServiceCaller(
	service: any,
	requestPayload?: any,
	errorHandler?: Function,
	disableBaseHandler?: boolean
) {
	try {
		const response: any = yield call(service, requestPayload);
		return response;
	} catch (error) {
		const _error = new ErrorInfo({
			code: error.code,
			documentLink: error.documentLink,
			exception: error.exception,
			hasValidation: error.hasValidation,
			internalMessage: error.internalMessage,
			message: error.message,
			validations: error.validations,
			footPrint: error.footPrint
		});
		if (!disableBaseHandler) {
			yield MessageBox.error({
				title: _error.code,
				content: _error.message,
				buttons: MessageBoxButtons.OK
			});
		}
		if (errorHandler) {
			errorHandler();
		}
		return _error;
	}
}

/**
 *
 * @param formPanelId WebInterfaceBaseState state's forms panelId example formPanelId definition: "0342083c-1793-9fc8-d4c6-7380a4173cce_Document1_default"
 */
export function* removeFormViewersFromState(formPanelId: string) {
	const formId = formPanelId.split('_')[0];
	const forms: Map<string, FormsEntities.FormsState> = yield select(
		(state: WebInterfaceBaseState) => state.getIn(['forms'])
	);
	const formViewerNames: Array<string> = yield select(
		(state: WebInterfaceBaseState) =>
			state
				.getIn(['forms', formPanelId, 'items', 'entities', 'items'])
				?.valueSeq()
				?.filter(control => control.type === 'FormViewer')
				?.map(control => control.id)
				?.toJS() || []
	);
	const repeaterNames: Array<string> = yield select(
		(state: WebInterfaceBaseState) =>
			state
				.getIn(['forms', formPanelId, 'items', 'entities', 'items'])
				?.valueSeq()
				?.filter(control => control.type === 'Repeater')
				?.map(control => control.id)
				?.toJS() || []
	);

	for (const name of formViewerNames) {
		const formViewerProperties = yield select((state: WebInterfaceBaseState) =>
			state.getIn(['forms', formPanelId, 'items', 'entities', 'items', name, 'properties'])
		);
		const formViewerPanelName = `${formId}_FormViewer_${name}_${formViewerProperties.get(
			'viewName'
		)}_${formViewerProperties.get('documentId')}`;
		yield put(FormWillCloseAction({ data: formViewerPanelName }));
	}

	for (const name of repeaterNames) {
		const repeaterProperties = yield select((state: WebInterfaceBaseState) =>
			state.getIn(['forms', formPanelId, 'items', 'entities', 'items', name, 'properties'])
		);

		for (const formKey of forms.keySeq()) {
			if (
				formKey.includes(
					`${formId}_Repeater_${name}_${repeaterProperties.get('viewName')}_`
				)
			) {
				yield put(FormWillCloseAction({ data: formKey }));
			}
		}
	}
}

export function* openNewPanelInsteadOfTheOtherOne(
	closePanelId: string,
	openPanelParams: IOpenNewPanelPayload,
	disableWillOpenStartUpPanel = false,
	closeUnpinnedPanels = false,
	dontOpenNewPanelIfDidntCloseSelectedPanel = false
) {
	let doesntClosedPanelIdList: string[] = [];
	if (closePanelId) {
		doesntClosedPanelIdList = yield call(
			closeFlexPanelTransaction,
			closePanelId,
			'',
			true,
			disableWillOpenStartUpPanel
		);
	}
	if (closeUnpinnedPanels) {
		yield put(
			closeUnpinnedFlexPanelsTransaction({
				panelId: openPanelParams.parent && openPanelParams.parent.panelGuid,
				panelType: openPanelParams.parent && openPanelParams.parent.panelType
			})
		);
	}
	yield delay(100);
	if (
		!dontOpenNewPanelIfDidntCloseSelectedPanel ||
		doesntClosedPanelIdList.findIndex(q => q == closePanelId) == -1
	) {
		yield put(panelWillUnMountTransaction({ panelId: closePanelId }));
		return yield call(openNewPanel, openPanelParams);
	}
	return null;
}

export function* openNewPanelWorkFlowClick(payload: WebInterfaceAction) {
	yield call(OpenFlowViewer, {
		projectName: payload.data.workflowItem.projectName,
		flowName: payload.data.workflowItem.flowName,
		projectVersion: payload.data.event.PackageVersion,
		parent: {
			panelGuid: payload.panelId,
			panelType: payload.panelType
		},
		processId: payload.data.event.processId,
		requestId: payload.data.event.requestId,
		fastEventId: payload.data.event.Id,
		then: payload.data.then,
		catch: payload.data.catch,
		hideHistoryAfterEvent: false
	});
}

/**
 * Converts mutable items to immutable and sets ML properties.
 * @param items Array of mutable items that contains ML properties.
 * @param Factory Factory function for converting items to immutable
 * @param properties Array of ML property names
 *
 * Ex => `let contents: List<ContentItem> = yield call(genericMLMap, response.items, ContentItemFactoryFromJS, ['name', 'description']);`
 */
export function* genericMLMap<T>(items: any[], Factory: Function, properties: string[]) {
	let _items = List<T>();
	try {
		for (let i = 0; i < items.length; i++) {
			const item = items[i];
			const mlObj: any = {};
			for (let j = 0; j < properties.length; j++) {
				mlObj[properties[j]] = yield call(getMLProperty, item[properties[j]]);
			}
			_items = _items.push(
				Factory({
					...item,
					...mlObj
				})
			);
		}
		return _items;
	} catch (error) {
		console.log('LOG: error', error);
		throw new Error(error);
	}
}

export function catchError(err: ErrorInfo, serviceName?: string) {
	if (isEmpty(err?.exception?.data)) {
		BCNotification.error({ message: findMessage.get('100704') });
	} else {
		const errorCode = MainUtils.getTaleInnerException(err?.exception?.data?.exception);
		if (errorCode === 'WF.INVALID_CONTINUE_REQUEST' || errorCode === 'FRMW_CORE_0043') {
			MessageBox.error({
				title: findMessage.get('100926'),
				content: findMessage.get('103257'),
				buttons: MessageBoxButtons.OK,
				errorCode: `Code : ${err.code} | FootPrint : ${err.footPrint}`
			});
		} else {
			MessageBox.error({ error: err.exception.data });
		}
	}
	Mobile.MobileView.mobileServiceResponse(false, serviceName, err);
}
